/* ============================================================
   custom-swatches.js — Capala sibling swatch navigation polish
   ------------------------------------------------------------
   Loaded globally (deferred) via snippets/swatch-color-list.liquid.
   Pure progressive enhancement on the native Broadcast siblings:

   1. INTENT PREFETCH — when a shopper hovers/touches a sibling
      swatch, the sibling product page is fetched in the
      background so the eventual click renders near-instantly.
      Hover alone never navigates; the click stays in control.

   2. EAGER PDP PREFETCH — on product pages, all sibling pages in
      the colour picker are prefetched at idle time (taps on
      mobile give no meaningful hover window). Skipped for
      Save-Data / slow connections.

   3. HERO IMAGE PRELOAD — the sibling page's first gallery image
      is preloaded (exact responsive candidate, via its own
      srcset/sizes) so the photo is ready when the page opens.

   Note: cross-document View Transitions were trialled and removed
   — the theme's preloader + entrance-animation systems produced
   visible seams ("double flash"). Swatch navigation remains a
   standard (fast, prefetched) navigation with the brand preloader.
   A true in-place swap is a separate scoped project.
   ============================================================ */

(function () {
  'use strict';

  /* ---------------- prefetch core ---------------- */

  var prefetched = new Set();
  var supportsPrefetch = (function () {
    var link = document.createElement('link');
    return !!(link.relList && link.relList.supports && link.relList.supports('prefetch'));
  })();

  function connectionAllowsEager() {
    var c = navigator.connection;
    if (!c) return true;
    if (c.saveData) return false;
    if (c.effectiveType && /(^|-)2g/.test(c.effectiveType)) return false;
    return true;
  }

  function prefetch(url) {
    if (!url || url.indexOf('/products/') === -1 || prefetched.has(url)) return;
    prefetched.add(url);

    if (supportsPrefetch) {
      var link = document.createElement('link');
      link.rel = 'prefetch';
      link.href = url;
      link.as = 'document';
      document.head.appendChild(link);
    } else {
      // Safari has no <link rel="prefetch">: best-effort cache warm.
      try {
        fetch(url, { credentials: 'same-origin', mode: 'same-origin' }).catch(function () {});
      } catch (e) { /* no-op */ }
    }
  }

  // Resolve a sibling URL from either surface:
  // PDP picker: <a class="sibling__link" href="...">
  // PLP card:   <div class="swatch__button" data-sibling-link="...">
  function siblingUrl(target) {
    if (!target || !target.closest) return null;
    var a = target.closest('a.sibling__link[href]');
    if (a) return a.getAttribute('href');
    var holder = target.closest('[data-sibling-link]');
    if (holder) return holder.getAttribute('data-sibling-link');
    return null;
  }

  /* ------------- hero image preload ------------- */
  // The biggest visual gap after a fast navigation is the product
  // hero image arriving late. Fetch the sibling page's HTML, lift the
  // first gallery <img>, and replay its srcset/sizes through an
  // off-screen Image so the browser caches the exact candidate it
  // will request on the real navigation.

  var heroPreloaded = new Set();

  function preloadSiblingHero(url) {
    if (!url || heroPreloaded.has(url) || !connectionAllowsEager()) return;
    heroPreloaded.add(url);
    try {
      fetch(url, { credentials: 'same-origin', mode: 'same-origin' })
        .then(function (r) { return r.ok ? r.text() : null; })
        .then(function (html) {
          if (!html) return;
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var img = doc.querySelector('.product__slides img');
          var pre;
          if (img && (img.getAttribute('srcset') || img.getAttribute('src'))) {
            pre = new Image();
            var sizes = img.getAttribute('sizes');
            var srcset = img.getAttribute('srcset');
            var src = img.getAttribute('src');
            if (sizes) pre.sizes = sizes;
            if (srcset) pre.srcset = srcset;
            if (src) pre.src = src;
          } else {
            var og = doc.querySelector('meta[property="og:image:secure_url"], meta[property="og:image"]');
            if (og && og.getAttribute('content')) {
              pre = new Image();
              pre.src = og.getAttribute('content');
            }
          }
        })
        .catch(function () { /* no-op */ });
    } catch (e) { /* no-op */ }
  }

  /* ------------- 1. intent prefetch (both surfaces) ------------- */

  ['pointerover', 'touchstart', 'focusin'].forEach(function (evt) {
    document.addEventListener(evt, function (e) {
      var url = siblingUrl(e.target);
      if (!url) return;
      prefetch(url);
      // Hero preload on intent, PDP picker only (PLP cards already
      // hover-swap their own image via the theme).
      if (e.target.closest && e.target.closest('.product__siblings')) {
        preloadSiblingHero(url);
      }
    }, { capture: true, passive: true });
  });

  /* ------------- 2. eager prefetch of PDP picker siblings ------------- */

  function eagerPrefetch() {
    if (!connectionAllowsEager()) return;
    var links = document.querySelectorAll('.product__siblings a.sibling__link[href]:not(.sibling__link--current)');
    for (var i = 0; i < links.length && i < 10; i++) {
      var href = links[i].getAttribute('href');
      prefetch(href);
      if (i < 6) preloadSiblingHero(href);
    }
  }

  function atIdle(fn) {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(fn, { timeout: 4000 });
    } else {
      setTimeout(fn, 2000);
    }
  }

  if (document.readyState === 'complete') {
    atIdle(eagerPrefetch);
  } else {
    window.addEventListener('load', function () { atIdle(eagerPrefetch); });
  }
})();
